import { useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INTERNAL_ROLE_KEYS,
  ROLE_LABELS,
  conditionOperators,
  formatMoney,
  operatorForField,
  uncoveredAmounts,
  fromMinor,
  type Currency,
  type RoleKey,
} from '@fpc/shared';
import type { ApprovalRuleView } from '@fpc/api-client';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useMoney } from '@/hooks/useMoney';
import { humanize, rupeesToMinor } from '@/lib/format';
import {
  Card,
  EmptyState,
  ErrorState,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
} from '@/components/ui';

type Condition = { field: string; operator: string; value: unknown };
type Step = {
  order: number;
  approverType: string;
  roleKey?: string;
  userId?: string;
  label?: string;
};

const AMOUNT_FIELDS = new Set(['amount']);

/** Condition fields whose value is a master record, picked by name rather than typed as an id. */
const REFERENCE_FIELDS = [
  { field: 'vendorId', label: 'Vendor' },
  { field: 'departmentId', label: 'Department' },
  { field: 'businessUnitId', label: 'Business unit' },
  { field: 'verticalId', label: 'Vertical' },
  { field: 'locationId', label: 'Location' },
  { field: 'regionId', label: 'Region' },
] as const;
type ReferenceField = (typeof REFERENCE_FIELDS)[number]['field'];
const isReferenceField = (field: string): field is ReferenceField =>
  REFERENCE_FIELDS.some((entry) => entry.field === field);

type Option = { id: string; name: string };
type Lookups = Record<ReferenceField, Option[]> & { users: Option[] };

/**
 * The company's masters, so conditions and steps name real records. A rule
 * compares ids on the invoice; a typed name or id would silently never match.
 */
function useLookups(companyId: string | undefined): Lookups {
  const query = { companyId, pageSize: 200 };
  const key = (name: string) => [name, 'for-approval-rules', companyId];
  const vendors = useQuery({
    queryKey: key('vendors'),
    queryFn: () => api.settings.vendors(query),
  });
  const departments = useQuery({
    queryKey: key('departments'),
    queryFn: () => api.settings.departments(query),
  });
  const units = useQuery({
    queryKey: key('business-units'),
    queryFn: () => api.settings.businessUnits(query),
  });
  const verticals = useQuery({
    queryKey: key('verticals'),
    queryFn: () => api.settings.verticals(query),
  });
  const locations = useQuery({
    queryKey: key('locations'),
    queryFn: () => api.settings.locations(query),
  });
  const regions = useQuery({
    queryKey: key('regions'),
    queryFn: () => api.settings.regions(query),
  });
  const users = useQuery({ queryKey: key('users'), queryFn: () => api.settings.users(query) });

  return {
    vendorId: vendors.data?.items ?? [],
    departmentId: departments.data?.items ?? [],
    businessUnitId: units.data?.items ?? [],
    verticalId: verticals.data?.items ?? [],
    locationId: locations.data?.items ?? [],
    regionId: regions.data?.items ?? [],
    users: users.data?.items ?? [],
  };
}

/** Keeps a condition's value in the shape its operator expects. */
function reshapeValue(value: unknown, operator: string): unknown {
  const list = Array.isArray(value) ? value : value === '' || value == null ? [] : [value];
  if (operator === 'in' || operator === 'nin') return list;
  if (operator === 'between') return [list[0] ?? 0, list[1] ?? 0];
  return list[0] ?? '';
}

/**
 * The value side of a condition: a picker for master records, a single or
 * paired amount, or plain text for the rest.
 */
function ConditionValue({
  condition,
  options,
  onChange,
}: {
  condition: Condition;
  options?: Option[];
  onChange(value: unknown): void;
}) {
  const { company } = useMoney();
  const many = condition.operator === 'in' || condition.operator === 'nin';

  if (options) {
    return many ? (
      <select
        multiple
        className="input h-24 w-56"
        value={(Array.isArray(condition.value) ? condition.value : []).map(String)}
        onChange={(event) =>
          onChange(Array.from(event.target.selectedOptions, (option) => option.value))
        }
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    ) : (
      <select
        className="input w-56"
        value={typeof condition.value === 'string' ? condition.value : ''}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Select…</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  }

  const isAmount = AMOUNT_FIELDS.has(condition.field);
  const unit = company.currency === 'INR' ? '₹' : company.currency;
  const toInput = (value: unknown) =>
    isAmount && typeof value === 'number' ? String(fromMinor(value)) : String(value ?? '');
  const fromInput = (text: string) =>
    isAmount
      ? (rupeesToMinor(text) ?? 0)
      : condition.field === 'employeeCount'
        ? Number(text)
        : text;

  if (condition.operator === 'between') {
    const [low, high] = Array.isArray(condition.value) ? condition.value : [0, 0];
    return (
      <span className="flex items-center gap-2">
        <input
          className="input w-32"
          value={toInput(low)}
          placeholder={isAmount ? `${unit} from` : 'from'}
          onChange={(event) => onChange([fromInput(event.target.value), high])}
        />
        <span className="text-sm text-slate-500">and</span>
        <input
          className="input w-32"
          value={toInput(high)}
          placeholder={isAmount ? `${unit} up to` : 'up to'}
          onChange={(event) => onChange([low, fromInput(event.target.value)])}
        />
      </span>
    );
  }

  return (
    <input
      className="input w-40"
      value={
        many && Array.isArray(condition.value)
          ? condition.value.join(', ')
          : toInput(condition.value)
      }
      placeholder={isAmount ? `${unit} amount` : many ? 'comma-separated values' : 'value'}
      onChange={(event) =>
        onChange(
          many
            ? event.target.value.split(',').map((part) => fromInput(part.trim()))
            : fromInput(event.target.value),
        )
      }
    />
  );
}

function nameOf(options: Option[], id: unknown): string {
  return options.find((option) => option.id === id)?.name ?? String(id);
}

/**
 * Approval rule builder — PRD §15, §36 `/settings/approvals`.
 *
 * Rules are data, so this screen edits conditions and steps directly. The
 * simulator answers the question an administrator actually has — "who would
 * have to approve a ₹35.4L invoice?" — before the rule is saved.
 */
export function ApprovalRulesPage() {
  const { companyId, can } = useAuth();
  const canEdit = can('approval_rule:update');
  const { company } = useMoney();
  const [editing, setEditing] = useState<ApprovalRuleView | 'new' | null>(null);
  const [simulating, setSimulating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['approval-rules', companyId],
    queryFn: () => api.settings.approvalRules({ companyId, pageSize: 100 }),
  });

  // So "Applies when: Department is ..." shows a name instead of a raw id.
  const lookups = useLookups(companyId);

  return (
    <>
      <PageHeader
        title="Approval Rules"
        subtitle="Who has to approve what. Rules are evaluated highest priority first."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setSimulating(true)}>
              Test a rule
            </button>
            {can('approval_rule:create') ? (
              <button className="btn-primary" onClick={() => setEditing('new')}>
                New rule
              </button>
            ) : null}
          </>
        }
      />

      {data?.items.length ? (
        <CoverageWarnings rules={data.items} currency={company.currency} />
      ) : null}

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorState error={error} />
      ) : !data?.items.length ? (
        <Card>
          <EmptyState
            illustration="approved"
            title="No approval rules configured"
            hint="Without a rule, submitted invoices are approved automatically and the reason is recorded in the audit trail."
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {data.items.map((rule) => (
            // The whole card opens the rule's editor, for whoever may change it.
            <div
              key={rule.id}
              className={`card p-4 sm:p-5 ${
                canEdit ? 'cursor-pointer transition-colors hover:border-brand-300' : ''
              }`}
              {...(canEdit
                ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': `Edit ${rule.name}`,
                    onClick: () => setEditing(rule),
                    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setEditing(rule);
                      }
                    },
                  }
                : {})}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  {/* Wraps on a phone, so a long rule name keeps its width. */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h2 className="w-full font-semibold sm:w-auto">{rule.name}</h2>
                    <StatusBadge status={rule.active ? 'ACTIVE' : 'INACTIVE'} />
                    <span className="text-xs text-slate-500">Priority {rule.priority}</span>
                    <span className="text-xs text-slate-500">{humanize(rule.appliesTo)}</span>
                  </div>
                  {rule.description ? (
                    <p className="mt-1 text-sm text-slate-500">{rule.description}</p>
                  ) : null}

                  <p className="mt-3 text-sm">
                    <span className="text-slate-500">Applies when: </span>
                    {rule.conditions.length
                      ? rule.conditions
                          .map((condition) =>
                            describeCondition(condition, lookups, company.currency),
                          )
                          .join(' and ')
                      : 'always'}
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                    {rule.steps
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .map((step, index) => (
                        <span key={step.order} className="flex items-center gap-2">
                          {index > 0 ? <span className="text-slate-300">→</span> : null}
                          <span className="rounded bg-slate-100 px-2 py-1">
                            {describeStep(step, lookups)}
                          </span>
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <RuleEditor
          rule={editing}
          otherRules={(data?.items ?? []).filter(
            (rule) => editing === 'new' || rule.id !== editing.id,
          )}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {simulating ? <SimulatorModal onClose={() => setSimulating(false)} /> : null}
    </>
  );
}

/**
 * Amount bands no general rule covers. An item there that misses every
 * vendor / department / other specific rule is approved automatically — easy
 * to miss when every rule on screen is an exception.
 */
function CoverageWarnings({ rules, currency }: { rules: ApprovalRuleView[]; currency: string }) {
  const format = (minor: number) => formatMoney(minor, currency as Currency);
  const subjects = [
    { appliesTo: 'VENDOR_INVOICE', label: 'Invoices' },
    { appliesTo: 'PAYROLL_BATCH', label: 'Payroll batches' },
  ];
  const warnings = subjects.flatMap(({ appliesTo, label }) => {
    if (!rules.some((rule) => rule.appliesTo === appliesTo)) return [];
    const gaps = uncoveredAmounts(rules, appliesTo);
    if (!gaps.length) return [];
    const bands = gaps
      .map((gap) =>
        gap.high === null
          ? `above ${format(gap.low - 1)}`
          : gap.low === gap.high
            ? `exactly ${format(gap.low)}`
            : `${format(gap.low)} to ${format(gap.high)}`,
      )
      .join(', ');
    return [`${label} ${bands} that match no other rule are approved automatically.`];
  });
  if (!warnings.length) return null;
  return (
    <div className="notice-warning mb-4 text-sm" role="status">
      {warnings.map((warning) => (
        <p key={warning}>{warning}</p>
      ))}
      <p className="mt-1">Add an amount-only rule for that range if they should be approved.</p>
    </div>
  );
}

/** How each operator reads — in the picker and in the rule summary alike. */
const OPERATOR_LABELS: Record<string, string> = {
  eq: 'is',
  ne: 'is not',
  gt: 'is above',
  gte: 'is at least',
  lt: 'is below',
  lte: 'is at most',
  in: 'is one of',
  nin: 'is not one of',
  between: 'is between',
};

/**
 * A condition whose operator its field cannot take (a vendor "is at least"…),
 * moved to the field's first operator. Rules saved before the editor kept the
 * two in step can hold one; it never matched, so the editor shows and saves
 * what the picker displays rather than the dead comparison.
 */
function repairCondition(condition: Condition): Condition {
  const operator = operatorForField(condition.field, condition.operator);
  if (operator === condition.operator) return condition;
  return { ...condition, operator, value: reshapeValue(condition.value, operator) };
}

// Rule bands are in the company's own currency, like the invoices they judge.
function describeCondition(condition: Condition, lookups: Lookups, currency: string): string {
  const operator = OPERATOR_LABELS[condition.operator] ?? condition.operator;

  const values = Array.isArray(condition.value) ? condition.value : [condition.value];
  const field = condition.field;
  const value = AMOUNT_FIELDS.has(field)
    ? values.map((entry) => formatMoney(Number(entry), currency as Currency)).join(' and ')
    : isReferenceField(field)
      ? values.map((entry) => nameOf(lookups[field], entry)).join(', ')
      : values.map(String).join(', ');

  const label = REFERENCE_FIELDS.find((entry) => entry.field === field)?.label ?? humanize(field);
  return `${label} ${operator} ${value}`;
}

function describeStep(step: Step, lookups?: Lookups): string {
  if (step.approverType === 'DEPARTMENT_HEAD') return step.label || 'Department Head';
  if (step.approverType === 'VERTICAL_HEAD') return step.label || 'Vertical Head';
  if (step.approverType === 'USER') {
    return step.label || (lookups ? nameOf(lookups.users, step.userId) : 'Named approver');
  }
  if (step.roleKey) return ROLE_LABELS[step.roleKey as RoleKey] ?? step.roleKey;
  return step.label || humanize(step.approverType);
}

function RuleEditor({
  rule,
  otherRules,
  onClose,
}: {
  rule: ApprovalRuleView | 'new';
  otherRules: ApprovalRuleView[];
  onClose(): void;
}) {
  const { companyId } = useAuth();
  const { company } = useMoney();
  const queryClient = useQueryClient();
  const isNew = rule === 'new';

  // Backs every reference picker: a condition's value must be the real id the
  // engine compares against on an invoice, not free text.
  const lookups = useLookups(companyId);

  const [name, setName] = useState(isNew ? '' : rule.name);
  const [description, setDescription] = useState(isNew ? '' : (rule.description ?? ''));
  const [appliesTo, setAppliesTo] = useState(isNew ? 'VENDOR_INVOICE' : rule.appliesTo);
  const [priority, setPriority] = useState(isNew ? 100 : rule.priority);
  const [active, setActive] = useState(isNew ? true : rule.active);
  const [conditions, setConditions] = useState<Condition[]>(
    isNew ? [] : rule.conditions.map(repairCondition),
  );
  // Said out loud: opening an old rule must not quietly change what it does.
  const repaired = isNew
    ? 0
    : rule.conditions.filter((condition) => repairCondition(condition) !== condition).length;
  const [steps, setSteps] = useState<Step[]>(
    isNew ? [{ order: 1, approverType: 'ROLE', roleKey: 'FINANCE_MANAGER' }] : rule.steps,
  );

  const samePriority = otherRules.filter(
    (other) => other.active && other.appliesTo === appliesTo && other.priority === priority,
  );

  const save = useMutation({
    mutationFn: () => {
      const body = {
        companyId,
        name,
        description: description || undefined,
        appliesTo,
        priority,
        active,
        conditions,
        steps: steps.map((step, index) => ({ ...step, order: index + 1 })),
      };
      return isNew
        ? api.settings.createApprovalRule(body)
        : api.settings.updateApprovalRule(rule.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-rules'] });
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.settings.deleteApprovalRule((rule as ApprovalRuleView).id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-rules'] });
      onClose();
    },
  });

  return (
    <Modal
      title={isNew ? 'New approval rule' : 'Edit approval rule'}
      wide
      onClose={onClose}
      footer={
        <>
          {!isNew ? (
            <button className="btn-danger mr-auto" onClick={() => remove.mutate()}>
              Deactivate
            </button>
          ) : null}
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!name || steps.length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save rule'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="name">
            Rule name
          </label>
          <input
            id="name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            // The example is in the company's own currency, like the bands.
            placeholder={
              company.currency === 'INR'
                ? 'Above ₹10L — Department Head, Finance Head, CFO'
                : `Above ${company.currency} 50,000 — Department Head, Finance Head, CFO`
            }
          />
        </div>
        <div>
          <label className="label" htmlFor="description">
            Description
          </label>
          <input
            id="description"
            className="input"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="appliesTo">
            Applies to
          </label>
          <select
            id="appliesTo"
            className="input"
            value={appliesTo}
            onChange={(event) => setAppliesTo(event.target.value as typeof appliesTo)}
          >
            <option value="VENDOR_INVOICE">Vendor invoices</option>
            <option value="PAYROLL_BATCH">Payroll batches</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="priority">
            Priority
          </label>
          <input
            id="priority"
            type="number"
            className="input"
            value={priority}
            onChange={(event) => setPriority(Number(event.target.value))}
          />
          <p className="mt-1 text-xs text-slate-500">
            Higher wins when several rules match. On a tie, the rule with more conditions wins, then
            the older rule.
          </p>
        </div>
      </div>

      {active && samePriority.length ? (
        <p className="notice-warning mt-4 text-sm">
          {samePriority.map((other) => other.name).join(', ')}{' '}
          {samePriority.length === 1 ? 'shares' : 'share'} priority {priority}. If both match an
          invoice, the tie is broken by specificity and then age — give this rule its own priority
          if the order matters.
        </p>
      ) : null}

      <section className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">Conditions</h3>
          <button
            className="btn-secondary"
            onClick={() =>
              setConditions([...conditions, { field: 'amount', operator: 'gte', value: 0 }])
            }
          >
            Add condition
          </button>
        </div>
        {repaired ? (
          <p className="notice-warning mb-2 text-sm">
            {repaired === 1 ? 'One condition was' : `${repaired} conditions were`} saved with a
            comparison its field cannot make (such as a vendor &ldquo;is at least&rdquo;), so it
            never matched. It now reads &ldquo;is&rdquo; — check it, then save to apply.
          </p>
        ) : null}
        {!conditions.length ? (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">
            No conditions — this rule applies to everything of the selected type.
          </p>
        ) : (
          <div className="space-y-2">
            {conditions.map((condition, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <select
                  className="input w-auto"
                  aria-label={`Condition ${index + 1} field`}
                  value={condition.field}
                  onChange={(event) => {
                    // A value only means something for the field it was picked for,
                    // and the operator must be one the new field can take — or the
                    // picker shows "is" while the rule stores something else.
                    const field = event.target.value;
                    const operator = operatorForField(field, condition.operator);
                    updateCondition(index, {
                      field,
                      operator,
                      value: reshapeValue('', operator),
                    });
                  }}
                >
                  <option value="amount">Amount</option>
                  {REFERENCE_FIELDS.map((entry) => (
                    <option key={entry.field} value={entry.field}>
                      {entry.label}
                    </option>
                  ))}
                  <option value="employeeCount">Employee count</option>
                </select>
                <select
                  className="input w-auto"
                  aria-label={`Condition ${index + 1} comparison`}
                  value={condition.operator}
                  onChange={(event) =>
                    updateCondition(index, {
                      operator: event.target.value,
                      value: reshapeValue(condition.value, event.target.value),
                    })
                  }
                >
                  {conditionOperators(condition.field).map((operator) => (
                    <option key={operator} value={operator}>
                      {OPERATOR_LABELS[operator]}
                    </option>
                  ))}
                </select>
                <ConditionValue
                  condition={condition}
                  options={isReferenceField(condition.field) ? lookups[condition.field] : undefined}
                  onChange={(value) => updateCondition(index, { value })}
                />
                <button
                  className="text-sm text-red-600"
                  onClick={() =>
                    setConditions(conditions.filter((_, position) => position !== index))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">Approval chain</h3>
          <button
            className="btn-secondary"
            onClick={() =>
              setSteps([
                ...steps,
                { order: steps.length + 1, approverType: 'ROLE', roleKey: 'CFO' },
              ])
            }
          >
            Add step
          </button>
        </div>
        <div className="space-y-2">
          {steps.map((step, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <span className="w-6 text-sm text-slate-500">{index + 1}.</span>
              <select
                className="input w-auto"
                value={step.approverType}
                onChange={(event) =>
                  updateStep(index, {
                    approverType: event.target.value,
                    roleKey: event.target.value === 'ROLE' ? (step.roleKey ?? 'CFO') : undefined,
                    userId: undefined,
                  })
                }
              >
                <option value="ROLE">Anyone with role</option>
                <option value="DEPARTMENT_HEAD">Department head</option>
                <option value="VERTICAL_HEAD">Vertical head</option>
                <option value="USER">Specific person</option>
              </select>
              {step.approverType === 'USER' ? (
                <select
                  className="input w-auto"
                  value={step.userId ?? ''}
                  onChange={(event) => updateStep(index, { userId: event.target.value })}
                >
                  <option value="">Select a person…</option>
                  {lookups.users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              ) : step.approverType === 'ROLE' ? (
                <select
                  className="input w-auto"
                  value={step.roleKey ?? ''}
                  onChange={(event) => updateStep(index, { roleKey: event.target.value })}
                >
                  {INTERNAL_ROLE_KEYS.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role as RoleKey]}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input w-48"
                  placeholder="Label (e.g. IT Head)"
                  value={step.label ?? ''}
                  onChange={(event) => updateStep(index, { label: event.target.value })}
                />
              )}
              {steps.length > 1 ? (
                <button
                  className="text-sm text-red-600"
                  onClick={() => setSteps(steps.filter((_, position) => position !== index))}
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Steps run in order. A rejection at any step stops the chain, and a submitter can never
          approve their own item. A missing department head falls back to the vertical head, then to
          anyone holding Approver; if two steps in a row land on the same single person, they decide
          once for both.
        </p>
      </section>

      <label className="mt-6 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={active}
          onChange={(event) => setActive(event.target.checked)}
        />
        Rule is active
      </label>

      {save.error ? (
        <div className="mt-4">
          <ErrorState error={save.error} />
        </div>
      ) : null}
    </Modal>
  );

  function updateCondition(index: number, patch: Partial<Condition>) {
    setConditions(
      conditions.map((entry, position) => (position === index ? { ...entry, ...patch } : entry)),
    );
  }
  function updateStep(index: number, patch: Partial<Step>) {
    setSteps(steps.map((entry, position) => (position === index ? { ...entry, ...patch } : entry)));
  }
}

/** "Who would approve ₹X?" — checked before a rule change goes live. */
function SimulatorModal({ onClose }: { onClose(): void }) {
  const { companyId } = useAuth();
  const { company } = useMoney();
  const lookups = useLookups(companyId);
  const [amount, setAmount] = useState('3540000');
  const [appliesTo, setAppliesTo] = useState('VENDOR_INVOICE');
  const [departmentId, setDepartmentId] = useState('');
  const [locationId, setLocationId] = useState('');

  // Only the leaves: the server derives unit, vertical and region from them,
  // exactly as it codes a real invoice, so rules on those axes match too.
  const simulate = useMutation({
    mutationFn: () =>
      api.settings.simulateApprovalRule({
        companyId,
        appliesTo,
        amount: rupeesToMinor(amount) ?? 0,
        departmentId: departmentId || undefined,
        locationId: locationId || undefined,
      }),
  });

  return (
    <Modal
      title="Test the approval rules"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn-primary" onClick={() => simulate.mutate()}>
            Evaluate
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="simType">
            Type
          </label>
          <select
            id="simType"
            className="input"
            value={appliesTo}
            onChange={(event) => setAppliesTo(event.target.value)}
          >
            <option value="VENDOR_INVOICE">Vendor invoice</option>
            <option value="PAYROLL_BATCH">Payroll batch</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="simAmount">
            Amount ({company.currency})
          </label>
          <input
            id="simAmount"
            className="input tabular"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="simDepartment">
            Department
          </label>
          <select
            id="simDepartment"
            className="input"
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            <option value="">Any</option>
            {lookups.departmentId.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="simLocation">
            Location
          </label>
          <select
            id="simLocation"
            className="input"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Any</option>
            {lookups.locationId.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {simulate.data ? (
        <div className="mt-5 rounded-md border border-slate-200 p-4">
          {simulate.data.matched ? (
            <>
              <p className="text-sm text-slate-500">Matching rule</p>
              <p className="font-semibold">{simulate.data.matched.name}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                {(simulate.data.matched.steps as Step[])
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((step, index) => (
                    <span key={step.order} className="flex items-center gap-2">
                      {index > 0 ? <span className="text-slate-300">→</span> : null}
                      <span className="rounded bg-slate-100 px-2 py-1">
                        {describeStep(step, lookups)}
                      </span>
                    </span>
                  ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-amber-800">{simulate.data.note}</p>
          )}
        </div>
      ) : null}

      {simulate.error ? (
        <div className="mt-4">
          <ErrorState error={simulate.error} />
        </div>
      ) : null}
    </Modal>
  );
}
